/**
 * Trip (shipment) logic for the console: labels, each bag's state on the trip, and the missing /
 * extra lists (F5.1, F5.2). Sources in uc-core:
 *   src/modules/store/services/shipment-query.service.ts  (GET /shipments/:id shape)
 *   src/modules/store/handlers/receive-shipment.handler.ts (TRANSIT_SHORTAGE / TRANSIT_EXTRA)
 *   src/modules/store/services/placement.service.ts        (a MISSING bag scanned later → TRANSIT_EXTRA)
 */
import type { ExceptionLite } from '../api/gate'
import type { ManifestItem } from '../api/store'
import { humanize } from '../labels'

export const SHIPMENT_STATUSES = ['LOADING', 'IN_TRANSIT', 'RECEIVED', 'RECEIVED_SHORT'] as const
export const SHIPMENT_STATUS_LABEL: Record<string, string> = {
  LOADING: 'Loading',
  IN_TRANSIT: 'In transit',
  RECEIVED: 'Received',
  RECEIVED_SHORT: 'Received short',
}

export const SHIPMENT_KINDS = ['TRANSFER', 'ISSUE', 'DISPATCH'] as const
export const SHIPMENT_KIND_LABEL: Record<string, string> = {
  TRANSFER: 'Transfer to store',
  ISSUE: 'Issue to processing',
  DISPATCH: 'Dispatch to customer',
}

export const shipmentStatusLabel = (s: string) => SHIPMENT_STATUS_LABEL[s] ?? humanize(s)
export const shipmentKindLabel = (k: string) => SHIPMENT_KIND_LABEL[k] ?? humanize(k)

/** Where one bag stands on this trip. */
export type BagTripState = 'LOADED' | 'UNLOADED' | 'MISSING' | 'VOID'
export const BAG_TRIP_STATE_LABEL: Record<BagTripState, string> = {
  LOADED: 'Loaded',
  UNLOADED: 'Unloaded',
  MISSING: 'Missing',
  VOID: 'Void',
}
/** Status-pill key per state (statusTones). */
export const BAG_TRIP_STATE_TONE: Record<BagTripState, string> = {
  LOADED: 'IN_TRANSIT',
  UNLOADED: 'RECEIVED',
  MISSING: 'VOID',
  VOID: 'VOID',
}

/**
 * Item `status` is the bag's CURRENT status (containers.status), so a bag unloaded here and later
 * voided still counts as unloaded on this trip; it only reads MISSING / VOID while not unloaded.
 */
export function bagTripState(item: Pick<ManifestItem, 'unloadedAt' | 'status'>): BagTripState {
  if (item.unloadedAt) return 'UNLOADED'
  if (item.status === 'MISSING') return 'MISSING'
  if (item.status === 'VOID') return 'VOID'
  return 'LOADED'
}

/** Check weight at the store minus the gate net (grams); null when not re-weighed. */
export function recheckDiffG(item: Pick<ManifestItem, 'recheckG' | 'netGateG'>): number | null {
  if (item.recheckG === null || item.netGateG === null) return null
  return item.recheckG - item.netGateG
}

export const MISSING_REASON_LABEL: Record<string, string> = {
  NOT_SCANNED: 'Not scanned at unloading',
  NOT_ON_VEHICLE: 'Not on the vehicle',
}
export const EXTRA_KIND_LABEL: Record<string, string> = {
  MISSED_AT_LOADING: 'Not scanned at loading',
  FOUND_AFTER_MISSING: 'Found after being marked missing',
  SHOULD_BE_ELSEWHERE: 'Belongs at another place or trip',
}

export interface MissingBag {
  key: string
  containerId: string | null
  tag: string | null
  unitId: string | null
  unitCode: string | null
  netGateG: number | null
  reason: string | null
  exceptionId: string | null
}
export interface ExtraBag {
  key: string
  containerId: string | null
  tag: string | null
  unitId: string | null
  unitCode: string | null
  kind: string | null
  exceptionId: string
  open: boolean
}

const s = (v: unknown): string | null => (v === null || v === undefined || v === '' ? null : String(v))

/**
 * Missing = manifest bags still not unloaded and MISSING, joined with the trip's TRANSIT_SHORTAGE
 * exceptions (one row per bag). Extra = the trip's TRANSIT_EXTRA exceptions.
 */
export function missingAndExtra(
  bags: ReadonlyArray<ManifestItem>,
  exceptions: ReadonlyArray<ExceptionLite>,
): { missing: MissingBag[]; extra: ExtraBag[] } {
  const missing = new Map<string, MissingBag>()
  for (const b of bags) {
    if (bagTripState(b) !== 'MISSING') continue
    const key = b.containerId ?? b.itemId
    missing.set(key, {
      key,
      containerId: b.containerId,
      tag: b.qrPayload,
      unitId: b.unitId,
      unitCode: b.unitCode,
      netGateG: b.netGateG,
      reason: null,
      exceptionId: null,
    })
  }
  const byContainer = new Map(bags.filter((b) => b.containerId).map((b) => [b.containerId!, b]))
  for (const e of exceptions) {
    if (e.type !== 'TRANSIT_SHORTAGE') continue
    const containerId = e.subjectType === 'CONTAINER' ? e.subjectId : s(e.detail.container_id)
    const key = containerId ?? e.id
    const bag = containerId ? byContainer.get(containerId) : undefined
    const prev = missing.get(key)
    missing.set(key, {
      key,
      containerId,
      tag: prev?.tag ?? bag?.qrPayload ?? s(e.detail.qr_payload),
      unitId: prev?.unitId ?? bag?.unitId ?? s(e.detail.unit_id),
      unitCode: prev?.unitCode ?? bag?.unitCode ?? s(e.detail.unit_code),
      netGateG: prev?.netGateG ?? bag?.netGateG ?? null,
      reason: s(e.detail.reason_code),
      exceptionId: e.id,
    })
  }
  const extra: ExtraBag[] = exceptions
    .filter((e) => e.type === 'TRANSIT_EXTRA')
    .map((e) => {
      const containerId = e.subjectType === 'CONTAINER' ? e.subjectId : s(e.detail.container_id)
      const bag = containerId ? byContainer.get(containerId) : undefined
      return {
        key: e.id,
        containerId,
        tag: s(e.detail.qr_payload) ?? bag?.qrPayload ?? null,
        unitId: s(e.detail.unit_id) ?? bag?.unitId ?? null,
        unitCode: s(e.detail.unit_code) ?? bag?.unitCode ?? null,
        kind: s(e.detail.kind),
        exceptionId: e.id,
        open: e.status === 'OPEN',
      }
    })
  return { missing: [...missing.values()], extra }
}

export const BIZ_STEP_LABEL: Record<string, string> = {
  BAG_LOADED: 'Bag loaded',
  SHIPMENT_DISPATCHED: 'Trip left',
  SHIPMENT_RECEIVED: 'Trip received',
  BAG_UNLOADED: 'Bag unloaded',
  BAG_MISSING: 'Bag marked missing',
  PUT_AWAY: 'Put away',
  MOVED: 'Moved',
  BAG_QUARANTINED: 'Bag put on hold',
  CONTAINER_REPLACED: 'Tag replaced',
  SAMPLE_DISPATCHED: 'Sample sent to lab',
}
export const bizStepLabel = (step: string) => BIZ_STEP_LABEL[step] ?? humanize(step)

export interface TripEvent {
  id: string
  bizStep: string
  subjectType: string
  occurredAt: string | null
  actorId: string | null
  payload: Record<string, unknown>
}
export interface TripEventGroup {
  id: string
  bizStep: string
  subjectType: string
  at: string | null
  until: string | null
  count: number
  actorId: string | null
  payload: Record<string, unknown>
}

/**
 * Collapse runs of the same step on the same kind of thing (five BAG_LOADED in a row → one line
 * "5 bags loaded") so the trip timeline stays readable. Order is kept.
 */
export function groupTripEvents(events: ReadonlyArray<TripEvent>): TripEventGroup[] {
  const out: TripEventGroup[] = []
  for (const e of events) {
    const stage = s(e.payload.stage)
    const last = out[out.length - 1]
    const lastStage = last ? s(last.payload.stage) : null
    if (last && last.bizStep === e.bizStep && last.subjectType === e.subjectType && lastStage === stage && e.subjectType !== 'SHIPMENT') {
      last.count += 1
      last.until = e.occurredAt ?? last.until
      continue
    }
    out.push({
      id: e.id,
      bizStep: e.bizStep,
      subjectType: e.subjectType,
      at: e.occurredAt,
      until: e.occurredAt,
      count: 1,
      actorId: e.actorId,
      payload: e.payload,
    })
  }
  return out
}

/** "Bag loaded" ×5 → "5 bags loaded"; samples moved with a stage → "Sample loaded". */
export function tripEventTitle(g: Pick<TripEventGroup, 'bizStep' | 'subjectType' | 'count' | 'payload'>): string {
  const stage = s(g.payload.stage)
  if (g.subjectType === 'SAMPLE' && g.bizStep === 'MOVED' && stage) {
    const verb = stage.toLowerCase()
    return g.count > 1 ? `${g.count} samples ${verb}` : `Sample ${verb}`
  }
  const base = bizStepLabel(g.bizStep)
  if (g.count > 1 && base.startsWith('Bag ')) return `${g.count} bags ${base.slice(4)}`
  return g.count > 1 ? `${base} ×${g.count}` : base
}
